import { screens } from '../../store';
import { getIcon } from '../icon/icons';
import type { Events } from '../models/eventList';
import { getEventByID } from '../models/events/eventsAll';
import type { Items } from '../models/item.type';
import type { MapData } from '../models/map.type';
import {
  addItem,
  addModifier,
  addTrait,
  createPlayerInstance,
  type Player,
  type PlayerClass,
} from '../models/player.type';
import type { TraitType } from '../models/trait-registry';
import { memory } from './memory';
import type { MapNode } from '../models/map.type';
import type { StatusEffect } from '../models/status-effects.type';
import { Screen } from '../models/screen.type';
import { MapNodeType } from '../models/map.type';

/**
 * The on-disk save format.
 *
 * `saveGame` writes these shapes and `loadGame` reads them back, so the two have to
 * stay in step. Declaring the format here is what turns a drift between the writer
 * and the reader into a compile error instead of a broken save file. The indexed
 * access types (`MapNode['battle']`, `Player['dice']`) keep the save in lockstep with
 * the live models without restating them.
 */
type SavedNode = {
  id: number;
  group: number;
  type?: MapNodeType;
  icon?: string;

  visited: boolean;
  scouted: boolean;
  open: boolean;

  battle?: MapNode['battle'];
  /** Events are persisted by id and rehydrated via getEventByID. */
  event?: Events;
};

type SavedMap = {
  nodes: Array<SavedNode>;
  edges: MapData['edges'];

  /** These four hold node ids, not nodes. */
  innerNodes: Array<number>;
  middleNodes: Array<number>;
  outerNodes: Array<number>;
  edgeNodes: Array<number>;
};

type SavedStatusEffect = {
  /**
   * The live `Modifier.type` is declared as a bare `string`, but every value it
   * holds is a StatusEffect and `addModifier` requires that enum to rehydrate one.
   * The save format records the stricter, truer type; saveGame casts on the way in.
   */
  type: StatusEffect;
  stacks: Player['statusEffects'][number]['stacks'];
  duration: Player['statusEffects'][number]['duration'];
  properties: Player['statusEffects'][number]['properties'];
};

type SavedPlayer = {
  title: string;
  /** Enemies share the Player shape, but only a hero is ever saved. */
  type?: PlayerClass;
  archetype?: TraitType;
  healthMax: number;
  rerollMax: number;
  dice: Player['dice'];
  colour: string;

  statusEffects: Array<SavedStatusEffect>;
  /** Traits are persisted by type and rehydrated via addTrait. */
  traits: Array<TraitType>;
  healthCurrent: number;

  level: number;
  experience: number;
  money: number;
  reputation: number;
  /**
   * Inventory is persisted by item type, once per stack, and rehydrated via
   * addItem -- which stacks a repeat, so three bombs load back as three.
   */
  inventory: Array<Items>;
};

export function loadGame() {
  const map: SavedMap = memory.get('map');
  const nodes = map.nodes.map((node): MapNode => {
    let newNode: MapNode = {
      id: node.id,
      type: node.type,
      icon: node.icon,

      visited: node.visited,
      scouted: node.scouted,
      open: node.open,

      battle: node.battle,
      event: getEventByID(node.event as Events),

      visit: () => {
        if (newNode.type == MapNodeType.Battle || newNode.type == MapNodeType.Boss) {
          screens.push(Screen.BattleStart, { battle: newNode.battle });
          return new Promise((resolve) => {
            // Non-null: only reached when node.type is 'battle' or 'boss'.
            newNode.battle!.resolve = resolve;
          });
        } else if (newNode.type == MapNodeType.Event) {
          screens.push(Screen.Event, { event: newNode.event, map, node: newNode });
        }
      },

      image: getIcon(node.icon ?? ''),
      shape: node.visited ? 'circle' : 'image',
      color: node.visited ? 'white' : node.open ? 'lightgreen' : 'red',
      shadow: true,
      size: 40,
      group: node.group,
    };
    return newNode;
  });
  let centerX = document.body.offsetWidth / 2;
  let centerY = document.body.offsetHeight / 2;
  nodes[0].x = centerX;
  nodes[0].y = centerY;

  // The four id lists are written from the same save as `nodes`, so every id
  // resolves. An id that does not is a corrupt save: drop it rather than leave an
  // undefined hole in the map for the renderer to trip over.
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const resolve = (ids: Array<number>) =>
    ids.map((id) => byId.get(id)).filter((node): node is MapNode => node !== undefined);

  const loadedMap: MapData = {
    nodes: nodes,
    edges: map.edges,

    innerNodes: resolve(map.innerNodes),
    middleNodes: resolve(map.middleNodes),
    outerNodes: resolve(map.outerNodes),
    edgeNodes: resolve(map.edgeNodes),
  };

  const playerSave: SavedPlayer = memory.get('player');
  if (!playerSave.type) {
    throw new Error('Cannot load save: the stored player has no type.');
  }
  const player = createPlayerInstance(playerSave.type, playerSave.archetype);
  player.title = playerSave.title;
  player.healthMax = playerSave.healthMax;
  player.rerollMax = playerSave.rerollMax;
  player.dice = playerSave.dice;
  player.colour = playerSave.colour;
  const root = document.querySelector<HTMLElement>(':root');
  root?.style.setProperty('--player-colour', playerSave.colour);

  playerSave.statusEffects.forEach((modifier) => {
    addModifier(player, modifier.type, modifier.stacks, {
      ...modifier.properties,
      duration: modifier.duration,
    });
  });
  playerSave.traits.forEach((trait) => {
    addTrait(player, trait);
  });
  player.healthCurrent = playerSave.healthCurrent;

  player.level = playerSave.level;
  player.experience = playerSave.experience;
  player.money = playerSave.money;
  player.reputation = playerSave.reputation;
  playerSave.inventory.forEach((item: Items) => {
    addItem(player, item);
  });

  // console.log('loading', map, playerSave)
  // console.log('loading', loadedMap, player)

  return { map: loadedMap, player };
}

export function saveGame(map: MapData, player: Player) {
  if (!map.innerNodes) {
    return;
  }
  let mapData = {
    nodes: map.nodes.map((node) => {
      return {
        id: node.id,
        group: node.group,
        type: node.type,
        icon: node.icon,

        visited: node.visited,
        scouted: node.scouted,
        open: node.open,

        battle: node.battle,
        event: node.event?.id,
      };
    }),
    edges: map.edges,

    innerNodes: map.innerNodes.map((node) => node.id),
    middleNodes: map.middleNodes.map((node) => node.id),
    outerNodes: map.outerNodes.map((node) => node.id),
    edgeNodes: map.edgeNodes.map((node) => node.id),
  };
  memory.persist('map', mapData);

  let playerSave = {
    title: player.title,
    type: player.type as PlayerClass,
    archetype: player.archetype,
    healthMax: player.healthMax,
    rerollMax: player.rerollMax,
    dice: player.dice,
    colour: player.colour,

    statusEffects: player.statusEffects.map((modifier) => {
      return {
        type: modifier.type as StatusEffect,
        stacks: modifier.stacks,
        duration: modifier.duration,
        properties: modifier.properties,
      };
    }),
    traits: player.traits.map((trait) => trait.type as TraitType),
    healthCurrent: player.healthCurrent,

    level: player.level,
    experience: player.experience,
    money: player.money,
    reputation: player.reputation,
    inventory: player.inventory.flatMap((item) => Array(item.stacks).fill(item.type)),
  };
  memory.persist('player', playerSave);

  // console.log('saving', mapData, playerSave)
}

export function deleteGame() {
  memory.persist('map', '');
  memory.persist('player', '');
}

export function isSavedGame() {
  if (memory.get('map') == null || memory.get('map') == '') return false;
  return true;
}
